import { Box, Button, DialogActions, Divider, Typography } from "@mui/material";
import type { ReactNode } from "react";

interface Walk {
  /** No open step before this one. */
  isFirstStep: boolean;
  onBack: () => void;
  onCancel: () => void;
  /** While something is being saved, Cancel would only hide it, not stop it. */
  cancelDisabled?: boolean;
}

interface CreateProps extends Walk {
  mode: "create";
  /** No open step after this one: the button creates. */
  isLastStep: boolean;
  onContinue: () => void;
  continueDisabled?: boolean;
  onCreate: () => void;
  createDisabled?: boolean;
  createLabel: string;
  creating?: boolean;
}

interface EditProps extends Walk {
  mode: "edit";
  unsaved?: boolean;
  /** The form's own buttons, on the right. */
  children: ReactNode;
}

/** The small outlined and contained buttons of the edit footer; their case comes
 * from the page's theme, as every other button's does. */
export const FOOTER_BUTTON_SX = {
  fontWeight: 600,
  borderRadius: 1,
  fontSize: 12,
} as const;

/** The bottom of the user and client forms. Creating walks the steps with Back
 * and Continue and creates on the last one; editing saves from any step. */
export default function FormFooter(props: CreateProps | EditProps) {
  return (
    <>
      <Divider />
      <DialogActions
        disableSpacing
        sx={{
          px: 3,
          py: 1.5,
          gap: 1,
          flexDirection: "column",
          alignItems: "stretch",
        }}
      >
        {props.mode === "create" ? (
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            {props.isFirstStep ? (
              <Button
                variant="outlined"
                onClick={props.onCancel}
                disabled={props.cancelDisabled}
                sx={{ minWidth: 120 }}
              >
                Cancel
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={props.onBack}
                sx={{ minWidth: 120 }}
              >
                Back
              </Button>
            )}
            {props.isLastStep ? (
              <Button
                variant="contained"
                onClick={props.onCreate}
                disabled={props.createDisabled}
                sx={{ minWidth: 140 }}
              >
                {props.creating ? "Creating..." : props.createLabel}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={props.onContinue}
                disabled={props.continueDisabled}
                sx={{ minWidth: 140 }}
              >
                Continue
              </Button>
            )}
          </Box>
        ) : (
          <>
            {props.unsaved && (
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: "error.main" }}
              >
                Unsaved changes
              </Typography>
            )}
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                justifyContent: "flex-end",
              }}
            >
              <Box sx={{ display: "flex", gap: 1, mr: "auto" }}>
                {!props.isFirstStep && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={props.onBack}
                    sx={FOOTER_BUTTON_SX}
                  >
                    Back
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={props.onCancel}
                  disabled={props.cancelDisabled}
                  sx={FOOTER_BUTTON_SX}
                >
                  Cancel
                </Button>
              </Box>
              {props.children}
            </Box>
          </>
        )}
      </DialogActions>
    </>
  );
}
